import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import PageLayout from '../../components/ui/PageLayout';
import { TrendingUp, Target, Users, RefreshCw, BarChart3, AlertCircle } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import Icon from '../../components/AppIcon';

const FunnelAnalytics = () => {
  const { user } = useAuth();
  const [funnels, setFunnels] = useState([]);
  const [selectedFunnel, setSelectedFunnel] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState('30d');

  useEffect(() => {
    if (user?.id) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (selectedFunnel?.id) {
      loadFunnelLeads();
    }
  }, [selectedFunnel?.id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const { data, error: funnelError } = await supabase
        ?.from('sales_funnels')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.order('created_at', { ascending: false });

      if (funnelError) {
        console.warn('Error loading funnels:', funnelError?.message);
        setFunnels([]);
      } else {
        setFunnels(data || []);
        if (data?.length > 0) {
          setSelectedFunnel(data?.[0]);
        }
      }
    } catch (err) {
      setError('Failed to load funnel data');
    } finally {
      setLoading(false);
    }
  };

  const loadFunnelLeads = async () => {
    if (!selectedFunnel?.id) return;
    try {
      const { data, error: leadsError } = await supabase
        ?.from('funnel_leads')
        ?.select('*')
        ?.eq('funnel_id', selectedFunnel?.id)
        ?.order('created_at', { ascending: false });

      if (!leadsError) {
        setLeads(data || []);
      }
    } catch (err) {
      console.error('Error loading leads:', err);
    }
  };

  const metrics = {
    totalLeads: leads?.length || 0,
    convertedLeads: leads?.filter(l => l?.is_converted)?.length || 0,
    conversionRate: leads?.length > 0 ? ((leads?.filter(l => l?.is_converted)?.length / leads?.length) * 100)?.toFixed(1) : 0,
    avgScore: leads?.length > 0 ? Math.round(leads?.reduce((sum, l) => sum + (l?.lead_score || 0), 0) / leads?.length) : 0,
    totalValue: leads?.reduce((sum, l) => sum + (l?.total_value || 0), 0)
  };

  const stageData = [
    { name: 'New', count: leads?.filter(l => (l?.lead_score || 0) < 30)?.length },
    { name: 'Contacted', count: leads?.filter(l => (l?.lead_score || 0) >= 30 && (l?.lead_score || 0) < 50)?.length },
    { name: 'Qualified', count: leads?.filter(l => (l?.lead_score || 0) >= 50 && (l?.lead_score || 0) < 80)?.length },
    { name: 'Hot', count: leads?.filter(l => (l?.lead_score || 0) >= 80 && !l?.is_converted)?.length },
    { name: 'Converted', count: leads?.filter(l => l?.is_converted)?.length }
  ];

  if (loading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading funnel analytics...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Funnel Analytics</h1>
          <p className="text-muted-foreground">Track and analyze your sales funnel performance</p>
        </div>
        <div className="flex items-center space-x-3 mt-4 lg:mt-0">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e?.target?.value)}
            className="px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
          <button
            onClick={loadData}
            className="flex items-center space-x-2 px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm hover:bg-muted transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>
      {/* Error */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}
      {/* Funnel Selector */}
      {funnels?.length > 0 && (
        <div className="mb-6">
          <select
            value={selectedFunnel?.id || ''}
            onChange={(e) => setSelectedFunnel(funnels?.find(f => f?.id === e?.target?.value))}
            className="px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
          >
            {funnels?.map(f => <option key={f?.id} value={f?.id}>{f?.name}</option>)}
          </select>
        </div>
      )}
      {/* Metrics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {[
          { label: 'Total Leads', value: metrics?.totalLeads, Icon: Users, color: 'text-blue-600' },
          { label: 'Converted', value: metrics?.convertedLeads, Icon: TrendingUp, color: 'text-green-600' },
          { label: 'Conversion Rate', value: `${metrics?.conversionRate}%`, Icon: Target, color: 'text-purple-600' },
          { label: 'Avg Score', value: metrics?.avgScore, Icon: BarChart3, color: 'text-orange-600' }
        ]?.map((card, idx) => (
          <div key={idx} className="bg-card border border-border rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-muted-foreground">{card?.label}</span>
              <card.Icon className={`w-5 h-5 ${card?.color}`} />
            </div>
            <div className="text-3xl font-bold text-foreground">{card?.value}</div>
          </div>
        ))}
      </div>
      {/* Stage Distribution Chart */}
      {leads?.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-6 shadow-sm mb-8">
          <h3 className="text-lg font-semibold text-foreground mb-6">Lead Stage Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={stageData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      {/* Empty State */}
      {funnels?.length === 0 && (
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <BarChart3 className="w-16 h-16 text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold text-foreground mb-2">No Funnels Found</h3>
          <p className="text-muted-foreground">Create a sales funnel to start tracking analytics</p>
        </div>
      )}
    </PageLayout>
  );
};

export default FunnelAnalytics;